import React, { useState, useMemo } from 'react'
import { Card, Button, Badge, EmptyState } from './ui'
import { TELESALES_AGENTS } from '../lib/constants'
import { Plus, Trash2, Check, RotateCcw, ChevronLeft, ChevronRight } from 'lucide-react'
import { todayWIB } from './MyWorkToday'

function addDays(dateStr, n) {
  const d = new Date(dateStr + 'T00:00:00')
  d.setDate(d.getDate() + n)
  return d.toLocaleDateString('en-CA')
}
const prettyDate = (d) => new Date(d + 'T00:00:00').toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
const norm = (s) => (s || '').toString().trim().toLowerCase()

// Personal to-do list, distinct from lead follow-up tasks. Per agent, per date.
export default function Todos({
  todos = [], currentAgent = '', isSuperadmin = false,
  onAdd, onUpdate, onDelete,
}) {
  const [date, setDate] = useState(todayWIB())
  const [filterAgent, setFilterAgent] = useState('')
  const [title, setTitle] = useState('')
  const [time, setTime] = useState('')
  const [busy, setBusy] = useState(false)

  const items = useMemo(() => {
    let list = todos.filter(t => t.task_date === date)
    if (isSuperadmin) {
      if (filterAgent) list = list.filter(t => norm(t.agent) === norm(filterAgent))
    } else {
      list = list.filter(t => norm(t.agent) === norm(currentAgent))
    }
    return list.sort((a, b) => {
      const ad = a.status === 'completed' ? 1 : 0
      const bd = b.status === 'completed' ? 1 : 0
      if (ad !== bd) return ad - bd   // open first
      return (a.due_time || '99:99').localeCompare(b.due_time || '99:99')
    })
  }, [todos, date, filterAgent, isSuperadmin, currentAgent])

  const openCount = items.filter(t => t.status !== 'completed').length

  const add = async () => {
    const t = title.trim()
    if (!t || !onAdd) return
    setBusy(true)
    try {
      await onAdd({ agent: currentAgent, task_date: date, title: t, due_time: time || null, status: 'pending' })
      setTitle(''); setTime('')
    } finally { setBusy(false) }
  }
  const toggle = (t) => {
    if (!onUpdate) return
    onUpdate(t.id, { status: t.status === 'completed' ? 'pending' : 'completed', updated_at: new Date().toISOString() })
  }
  const canModify = (t) => isSuperadmin || norm(t.agent) === norm(currentAgent)

  return (
    <div>
      {/* Date nav */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <button onClick={() => setDate(addDays(date, -1))} style={navBtn}><ChevronLeft size={16} /></button>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#111', minWidth: 210, textAlign: 'center' }}>{prettyDate(date)}</div>
        <button onClick={() => setDate(addDays(date, 1))} style={navBtn}><ChevronRight size={16} /></button>
        <button onClick={() => setDate(todayWIB())} style={{ ...navBtn, width: 'auto', padding: '0 10px', fontSize: 12, fontWeight: 600 }}>Hari ini</button>
        <input type="date" value={date} onChange={e => setDate(e.target.value)}
          style={{ fontSize: 12, padding: '6px 9px', border: '0.5px solid rgba(0,0,0,0.2)', borderRadius: 8 }} />
        {isSuperadmin && (
          <select value={filterAgent} onChange={e => setFilterAgent(e.target.value)}
            style={{ marginLeft: 'auto', fontSize: 12, padding: '6px 10px', border: '0.5px solid rgba(0,0,0,0.2)', borderRadius: 8, fontWeight: 600 }}>
            <option value="">Semua agent</option>
            {TELESALES_AGENTS.map(a => <option key={a}>{a}</option>)}
          </select>
        )}
      </div>

      {/* Add form */}
      <Card style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 260px' }}>
            <label style={labelStyle}>Tugas pribadi</label>
            <input value={title} onChange={e => setTitle(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') add() }}
              placeholder="mis. Siapkan materi demo, balas email vendor…"
              style={inputStyle} />
          </div>
          <div style={{ width: 110 }}>
            <label style={labelStyle}>Jam (opsional)</label>
            <input type="time" value={time} onChange={e => setTime(e.target.value)} style={inputStyle} />
          </div>
          <Button variant="primary" size="sm" onClick={add} disabled={busy || !title.trim()}>
            <Plus size={13} /> Tambah
          </Button>
        </div>
      </Card>

      {/* List */}
      <Card>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#111' }}>Daftar tugas</span>
          <Badge variant="info">{openCount} belum selesai</Badge>
        </div>
        {items.length === 0 ? (
          <EmptyState>Belum ada tugas pribadi untuk tanggal ini.</EmptyState>
        ) : items.map(t => {
          const done = t.status === 'completed'
          return (
            <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 2px', borderBottom: '0.5px solid rgba(0,0,0,0.07)' }}>
              <button onClick={() => canModify(t) && toggle(t)} title={done ? 'Tandai belum selesai' : 'Tandai selesai'}
                style={{ width: 22, height: 22, borderRadius: 6, border: '1.5px solid ' + (done ? '#1D9E75' : 'rgba(0,0,0,0.25)'), background: done ? '#1D9E75' : '#fff', cursor: canModify(t) ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {done && <Check size={14} color="#fff" />}
              </button>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, color: done ? '#999' : '#111', textDecoration: done ? 'line-through' : 'none' }}>{t.title}</div>
                <div style={{ fontSize: 11, color: '#aaa', marginTop: 1 }}>
                  {t.due_time ? t.due_time + ' · ' : ''}{isSuperadmin ? t.agent : ''}
                </div>
              </div>
              {canModify(t) && (
                <div style={{ display: 'flex', gap: 6 }}>
                  {done && <button onClick={() => toggle(t)} title="Buka lagi" style={iconBtn}><RotateCcw size={14} /></button>}
                  <button onClick={() => onDelete && onDelete(t.id)} title="Hapus" style={{ ...iconBtn, color: '#E24B4A' }}><Trash2 size={14} /></button>
                </div>
              )}
            </div>
          )
        })}
      </Card>
    </div>
  )
}

const navBtn = { width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff', border: '0.5px solid rgba(0,0,0,0.2)', borderRadius: 8, cursor: 'pointer', color: '#333' }
const iconBtn = { background: 'none', border: '0.5px solid rgba(0,0,0,0.15)', borderRadius: 6, padding: '4px 6px', cursor: 'pointer', color: '#555', display: 'flex', alignItems: 'center' }
const labelStyle = { fontSize: 11, color: '#888', display: 'block', marginBottom: 3 }
const inputStyle = { width: '100%', fontSize: 12, padding: '7px 9px', border: '0.5px solid rgba(0,0,0,0.2)', borderRadius: 8, boxSizing: 'border-box', fontFamily: 'inherit' }
